import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Percent, Plus, Trash2, FileSpreadsheet } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { AccountantSend } from '../components/AccountantSend';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const money = (v) => `₪${(parseFloat(v) || 0).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ date: today(), description: '', amount: '', vatAmount: '' });

const ExpenseForm = ({ onAdded }) => {
  const [form, setForm] = useState(emptyForm());
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const add = async () => {
    if (!form.description || !form.amount) return toast({ title: 'שגיאה', description: 'יש למלא תיאור וסכום', variant: 'destructive' });
    await axios.post(`${API}/expenses`, { ...form, category: 'רכש/הוצאה' });
    setForm(emptyForm());
    onAdded();
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end print:hidden" data-testid="vat-expense-form">
      <Input type="date" value={form.date} onChange={set('date')} data-testid="vat-expense-date" />
      <Input placeholder="תיאור / ספק" value={form.description} onChange={set('description')} data-testid="vat-expense-description" />
      <Input type="number" placeholder="סכום כולל מע״מ" value={form.amount} onChange={set('amount')} data-testid="vat-expense-amount" />
      <Input type="number" placeholder="מע״מ ששולם" value={form.vatAmount} onChange={set('vatAmount')} data-testid="vat-expense-vat" />
      <Button onClick={add} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="vat-expense-add-btn"><Plus className="w-4 h-4 ml-1" />הוסף</Button>
    </div>
  );
};

export default function VatReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);

  const load = () => {
    axios.get(`${API}/reports/vat-monthly`, { params: { year } }).then((r) => setData(r.data)).catch(() => setData(null));
    axios.get(`${API}/expenses`).then((r) => setExpenses((r.data || []).filter((e) => String(e.date || '').startsWith(String(year))).sort((a, b) => String(b.date).localeCompare(String(a.date))))).catch(() => setExpenses([]));
  };
  useEffect(load, [year]);

  const remove = async (id) => { await axios.delete(`${API}/expenses/${id}`); load(); };
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const th = 'p-3 text-center font-bold border border-gray-300';
  const td = 'p-3 text-center border border-gray-200';

  return (
    <div className="min-h-screen py-10 px-4" dir="rtl" data-testid="vat-report-page">
      <div className="container mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-4xl font-black text-gray-900 flex items-center gap-3"><Percent className="w-9 h-9 text-indigo-600" />דוח מע"מ חודשי</h1>
          <div className="flex items-center gap-2 print:hidden">
            <Button onClick={() => { window.location.href = `${API}/reports/vat-monthly/excel?year=${year}`; }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="vat-export-excel-btn">
              <FileSpreadsheet className="w-4 h-4 ml-2" />ייצוא לאקסל
            </Button>
            <select value={year} onChange={(e) => setYear(+e.target.value)} className="p-2 text-lg border-2 rounded-md" data-testid="vat-year-select">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <p className="text-sm text-gray-500">מע"מ עסקאות לפי תאריך הרשומה (רישום יומי, סגירות עסקה, קריאות שירות), רק ברשומות שחויבו במע"מ. מע"מ תשומות לפי חשבוניות הרכש (סכום ומע"מ שהוזנו בדף חשבוניות רכש) ולפי ההוצאות שהוזנו למטה.</p>

        <Card className="shadow-lg">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="vat-report-table">
              <thead className="bg-indigo-600 text-white">
                <tr><th className={th}>חודש</th><th className={th}>מחזור לפני מע"מ</th><th className={th}>מע"מ עסקאות</th><th className={th}>הוצאות ורכש</th><th className={th}>מע"מ תשומות</th><th className={th}>לתשלום / להחזר</th></tr>
              </thead>
              <tbody>
                {(data?.months || []).map((m) => (
                  <tr key={m.month} className="hover:bg-indigo-50" data-testid={`vat-row-${m.month}`}>
                    <td className={`${td} font-semibold`}>{MONTHS[m.month - 1]}</td>
                    <td className={td}>{money(m.sales)}</td>
                    <td className={`${td} text-blue-700 font-semibold`}>{money(m.outputVat)}</td>
                    <td className={td}>{money(m.expenses)}</td>
                    <td className={`${td} text-orange-700 font-semibold`}>{money(m.inputVat)}</td>
                    <td className={`${td} font-bold ${m.netVat < 0 ? 'text-green-700' : m.netVat > 0 ? 'text-red-700' : 'text-gray-400'}`}>{m.netVat < 0 ? `החזר ${money(-m.netVat)}` : money(m.netVat)}</td>
                  </tr>
                ))}
              </tbody>
              {data && (
                <tfoot className="bg-indigo-100 font-black">
                  <tr data-testid="vat-totals-row">
                    <td className={td}>סה"כ {year}</td><td className={td}>{money(data.totals.sales)}</td><td className={td}>{money(data.totals.outputVat)}</td>
                    <td className={td}>{money(data.totals.expenses)}</td><td className={td}>{money(data.totals.inputVat)}</td><td className={td}>{money(data.totals.netVat)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>

        <AccountantSend year={year} defaultMonth={year === new Date().getFullYear() ? Math.max(1, new Date().getMonth()) : 12} />

        <Card className="shadow-lg">
          <CardHeader><CardTitle>הוצאות ורכש (מע"מ תשומות)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <ExpenseForm onAdded={load} />
            {expenses.length === 0 ? <p className="text-sm text-gray-400">אין הוצאות בשנה זו</p> : (
              <table className="w-full text-sm border-collapse" data-testid="vat-expenses-table">
                <thead><tr className="bg-gray-50"><th className={th}>תאריך</th><th className={th}>תיאור</th><th className={th}>סכום</th><th className={th}>מע"מ</th><th className={`${th} print:hidden`}></th></tr></thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr key={e.id} data-testid={`vat-expense-${e.id}`}>
                      <td className={td}>{e.date ? new Date(e.date).toLocaleDateString('he-IL') : '-'}</td><td className={td}>{e.description}</td>
                      <td className={td}>{money(e.amount)}</td><td className={td}>{money(e.vatAmount)}</td>
                      <td className={`${td} print:hidden`}><button type="button" onClick={() => remove(e.id)} className="text-red-500 hover:text-red-700" data-testid={`vat-expense-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
